import min from 'lodash/min'
import max from 'lodash/max'
import {
  getRectangleIntersections,
  horizontallyConnectRects,
  intersectsWithSome,
  removeNeedlessRectOverlapping,
  withoutEmptyRects
} from '../annotations/utils'
import {
  DEFAULT_POINT_ANNOTATION_COLOR,
  DEFAULT_INK_ANNOTATION_COLOR,
  DEFAULT_STRIKEOUT_ANNOTATION_COLOR,
  DEFAULT_AREA_ANNOTATION_COLOR,
  DEFAULT_FREETEXT_ANNOTATION_COLOR,
  DEFAULT_HIGHLIGHT_ANNOTATION_COLOR,
  DEFAULT_FREETEXT_BACKGROUND_COLOR,
  DEFAULT_FONT
} from '../constants'
import { isPenEvent } from '../utils'
import sessionStorage from "../../common/session-storage"
import { getAccessTokenFromAppState } from '../../utils'

const EMPTY_ARRAY = []

export function getCursorXOffset(type, isPenBeingUsed) {
  // keep for future use
  switch (type) {
    default:
      return 0
  }
}

export function getCursorYOffset(type, isPenBeingUsed) {
  switch (type) {
    case 'ink':
      return isPenBeingUsed ? 0 : 15
    default:
      return 0
  }
}

export function getPosition(element) {
  var rect = element.getBoundingClientRect()
  return { x: rect.left, y: rect.top }
}

export function flatten(arrays) {
  return arrays.reduce((a, b) => {
    return a.concat(b)
  }, EMPTY_ARRAY)
}

export function getPressure(event) {
  if (typeof event.pressure === 'number') {
    return event.pressure === 0 ? 0.5 : event.pressure
  }

  if (typeof event.force === 'number') {
    return event.pressure === 0 ? 0.5 : event.force
  }

  const nativeEvent =
    event.nativeEvent instanceof Event ? event.nativeEvent : event
  if (typeof nativeEvent.webkitForce === 'number') {
    const pressure =
      nativeEvent.webkitForce > 1.0
        ? Math.sqrt(nativeEvent.webkitForce - 1) // probably a trackpad
        : nativeEvent.webkitForce
    return pressure === 0 ? 0.5 : pressure
  }

  return 0.5
}

export function getTextSelectionRectangles() {
  const selection = window.getSelection()
  if (!selection.rangeCount) {
    return EMPTY_ARRAY
  }
  const range = selection.getRangeAt(0)
  return Array.from(range.getClientRects())
}

function withoutPageHuggers(viewportHeight, viewportWidth) {
  return rect =>
    rect.left > 1 &&
    rect.right < viewportWidth - 1 &&
    rect.bottom - rect.top < viewportHeight &&
    rect.right - rect.left < viewportWidth
}

function adjustToDivPosition(divPosition) {
  return rect => {
    return {
      left: rect.left - divPosition.x,
      top: rect.top - divPosition.y,
      right: rect.right - divPosition.x,
      bottom: rect.bottom - divPosition.y
    }
  }
}

export function returnRectangleRange(rects) {
  return [
    min(rects.map(rect => rect.left)),
    max(rects.map(rect => rect.right)),
    min(rects.map(rect => rect.top)),
    max(rects.map(rect => rect.bottom))
  ]
}

export function createPointPlacement(point, scale) {
  const placementWidthConstant = 4.66666 * scale
  const placementHeightConstant = 6.66666 * scale
  return [
    [point.x - placementWidthConstant, point.y + placementHeightConstant],
    [point.x + placementWidthConstant, point.y - placementHeightConstant]
  ]
}

export function createFreetextPlacement(point, scale) {
  const placementWidthConstant = 4.66666 * scale
  const placementHeightConstant = 10.33333 * scale
  return [
    [point.x - placementWidthConstant, point.y + placementHeightConstant],
    [point.x + placementWidthConstant, point.y - placementHeightConstant]
  ]
}

export function isTooSmall(minX, maxX, minY, maxY, minimumSize) {
  return (
    Math.abs(minX - maxX) < minimumSize && Math.abs(minY - maxY) < minimumSize
  )
}

export function createAreaAnnotation(id, paths, color, strokeWidth) {
  const path = paths[0]
  const pathMax = path[path.length - 1]
  const minX = min([path[0].x, pathMax.x])
  const maxX = max([path[0].x, pathMax.x])
  const minY = min([path[0].y, pathMax.y])
  const maxY = max([path[0].y, pathMax.y])
  const MINIMUM_SQUARE_SIZE = 5
  if (isTooSmall(minX, maxX, minY, maxY, MINIMUM_SQUARE_SIZE)) return null
  return {
    id,
    type: 'square',
    color: typeof color === 'string' ? color : DEFAULT_AREA_ANNOTATION_COLOR,
    opacity: 1,
    rect: [[minX, maxY], [maxX, minY]],
    subject: 'Rectangle',
    width: strokeWidth
  }
}

export function createPointAnnotation(id, paths, viewportScale, color) {
  const path = paths[0]
  const lastPoint = path[path.length - 1]
  return {
    id,
    type: 'text',
    rect: createPointPlacement(lastPoint, viewportScale),
    subject: 'Comment',
    icon: 'Comment',
    color: typeof color === 'string' ? color : DEFAULT_POINT_ANNOTATION_COLOR
  }
}

export function createHighlightAnnotation(
  id,
  viewportHeight,
  viewportWidth,
  viewportScale,
  color,
  divPosition,
  textLayerDivRects
) {
  const textSelectionRectangles = getTextSelectionRectangles()
    .map(adjustToDivPosition(divPosition))
    .filter(withoutPageHuggers(viewportHeight, viewportWidth))
  const adjustedTextLayerDivRects = (textLayerDivRects || [])
    .map(adjustToDivPosition(divPosition))
    .filter(intersectsWithSome(textSelectionRectangles))

  const finalSelectionRectangles = textSelectionRectangles
    .sort((a, b) => (a.left < b.left ? -1 : 1))
    .sort((a, b) => (a.bottom < b.bottom ? -1 : 1))
    .reduce(getRectangleIntersections(adjustedTextLayerDivRects), EMPTY_ARRAY)
    .reduce(horizontallyConnectRects, [])
    .reduce(removeNeedlessRectOverlapping, [])
    .filter(withoutEmptyRects)

  if (!finalSelectionRectangles.length) return null
  const [minX, maxX, minY, maxY] = returnRectangleRange(
    finalSelectionRectangles
  )
  const MINIMUM_HIGHLIGHT_SIZE = 5
  if (isTooSmall(minX, maxX, minY, maxY, MINIMUM_HIGHLIGHT_SIZE)) return null
  return {
    id,
    type: 'highlight',
    rect: [[minX, maxY], [maxX, minY]],
    coords: finalSelectionRectangles.map(rect => {
      return [
        [rect.left, rect.bottom],
        [rect.right, rect.bottom],
        [rect.left, rect.top],
        [rect.right, rect.top]
      ]
    }),
    color:
      typeof color === 'string' ? color : DEFAULT_HIGHLIGHT_ANNOTATION_COLOR,
    subject: 'Highlight'
  }
}

export function createStrikeoutAnnotation(
  id,
  viewportHeight,
  viewportWidth,
  viewportScale,
  color,
  divPosition,
  textLayerDivRects
) {
  const textSelectionRectangles = getTextSelectionRectangles()
    .map(adjustToDivPosition(divPosition))
    .filter(withoutPageHuggers(viewportHeight, viewportWidth))

  const adjustedTextLayerDivRects = textLayerDivRects
    .map(adjustToDivPosition(divPosition))
    .filter(intersectsWithSome(textSelectionRectangles))

  const finalSelectionRectangles = textSelectionRectangles
    .sort((a, b) => (a.left < b.left ? -1 : 1))
    .sort((a, b) => (a.bottom < b.bottom ? -1 : 1))
    .reduce(getRectangleIntersections(adjustedTextLayerDivRects), EMPTY_ARRAY)
    .reduce(horizontallyConnectRects, [])
    .reduce(removeNeedlessRectOverlapping, [])
    .filter(withoutEmptyRects)
  if (!finalSelectionRectangles.length) return null
  const [minX, maxX, minY, maxY] = returnRectangleRange(
    finalSelectionRectangles
  )
  const MINIMUM_STRIKEOUT_SIZE = 5
  if (isTooSmall(minX, maxX, minY, maxY, MINIMUM_STRIKEOUT_SIZE)) return null
  return {
    id,
    type: 'strikeout',
    rect: [[minX, maxY], [maxX, minY]],
    coords: finalSelectionRectangles.map(rect => {
      return [
        [rect.left, rect.bottom],
        [rect.right, rect.bottom],
        [rect.left, rect.top],
        [rect.right, rect.top]
      ]
    }),
    color:
      typeof color === 'string' ? color : DEFAULT_STRIKEOUT_ANNOTATION_COLOR,
    subject: 'Strikeout'
  }
}

export function createInkAnnotation(id, paths, color, strokeWidth) {
  const gestures = paths.map(gesture =>
    gesture.map(point => {
      const transformedPoint = {
        x: point.x,
        y: point.y
      }
      if (typeof point.width === 'number') {
        transformedPoint.width = point.width
      }
      return transformedPoint
    })
  )
  const allX = flatten(paths.map(gesture => gesture.map(point => point.x)))
  const allY = flatten(paths.map(gesture => gesture.map(point => point.y)))
  const minX = min(allX)
  const maxX = max(allX)
  const minY = min(allY)
  const maxY = max(allY)

  const MINIMUM_INK_SIZE = 2
  if (isTooSmall(minX, maxX, minY, maxY, MINIMUM_INK_SIZE)) return null
  return {
    id,
    type: 'ink',
    color: typeof color === 'string' ? color : DEFAULT_INK_ANNOTATION_COLOR,
    rect: [[minX, maxY], [maxX, minY]],
    inklist: { gestures },
    subject: 'Free Hand',
    width: strokeWidth || 2
  }
}

export function createFreetextAnnotation({
  id,
  paths,
  viewportHeight,
  viewportWidth,
  viewportScale,
  color,
  bgColor,
  font,
  strokeWidth,
  divPosition,
  textLayerDivRects
}) {
  const path = paths[0]
  const lastPoint = path[path.length - 1]
  return {
    id,
    type: 'freetext',
    color:
      typeof color === 'string' ? color : DEFAULT_FREETEXT_ANNOTATION_COLOR,
    bgColor:
      typeof bgColor === 'string' ? bgColor : DEFAULT_FREETEXT_BACKGROUND_COLOR,
    rect: createFreetextPlacement(lastPoint, viewportScale),
    subject: 'FreeText',
    font: font || DEFAULT_FONT,
    'text-align': 'left'
  }
}

export function getPointFromEvent(div, event, type, strokeWidth) {
  const divPosition = getPosition(div)
  let point

  let pressure
  if (
    event.targetTouches !== null &&
    typeof event.targetTouches === 'object' &&
    typeof event.targetTouches.item === 'function'
  ) {
    const touch = event.targetTouches.item(0)
    pressure = getPressure(touch)
    if (
      typeof touch.clientX !== 'number' ||
      typeof touch.clientY !== 'number'
    ) {
      console.warn('clientX or clientY missing from touch')
      return { x: 0, y: 0 }
    }
    point = {
      x: touch.clientX - divPosition.x,
      y: touch.clientY - divPosition.y
    }
  } else {
    pressure = getPressure(event)
    if (
      typeof event.clientX !== 'number' ||
      typeof event.clientY !== 'number'
    ) {
      console.warn('clientX or clientY missing from event')
      return { x: 0, y: 0 }
    }
    const clientX = event.clientX // satisfy Flow
    const clientY = event.clientY
    const isPenBeingUsed = isPenEvent(event)
    point = {
      x: clientX - divPosition.x + getCursorXOffset(type, isPenBeingUsed),
      y: clientY - divPosition.y + getCursorYOffset(type, isPenBeingUsed)
    }
  }
  if (pressure !== 0.5) {
    point.width = pressure * strokeWidth * 2
    // like dragging a pencil to draw a fatter line
    if (typeof event.tiltX === 'number' && event.tiltX > 0) {
      point.width *= 1 + Math.abs(event.tiltX) / 90
    }
    if (typeof event.tiltY === 'number' && event.tiltY > 0) {
      point.width *= 1 + Math.abs(event.tiltY) / 90
    }
  }
  return point
}

export function saveAnnotationToSession(annotation) {
  annotation['savedFromSession'] = true
  const annotations = sessionStorage.annotations
  let parsedAnnotations;
  const accessToken = getAccessTokenFromAppState()
  if (accessToken) {
    if (annotations) {
      parsedAnnotations = JSON.parse(annotations);
      if (parsedAnnotations[accessToken]) {
        const existingAnnotation = parsedAnnotations[accessToken].find(ann => ann?.id === annotation?.id)
        if (existingAnnotation) {
          parsedAnnotations[accessToken].forEach((ann, i) => {
            if (ann?.id === annotation?.id) {
              parsedAnnotations[accessToken][i] = annotation
            }
          })
        } else {
          parsedAnnotations[accessToken].push(annotation)
        }
      } else {
        parsedAnnotations[accessToken] = [annotation];
      }
    } else {
      parsedAnnotations = {
        [accessToken]: [annotation],
      }
    }
    sessionStorage.setItem('annotations', JSON.stringify(parsedAnnotations));
  }
}
